/**
 * Compact Search Bar Component
 *
 * Results page search bar with animated expansion.
 * Figma: HeroSearchBar (Node: 1:2991)
 *
 * States:
 * - Default: 224×35px input with "What are you looking for?" placeholder
 * - Expanded: 280×378.5px with gender options + recent searches
 *
 * Note: This component always starts a NEW search.
 * Animates width and height between states.
 */
"use client";

import { useEffect, useRef, useState } from "react";

import Image from "next/image";

import { motion } from "framer-motion";

import { useSearchContext } from "@/contexts/SearchContext";
import { colors } from "@/styles/design-tokens";
import { mapGenderToCatalog, mapGenderToDisplay } from "@/utils/genderMapping";

interface CompactSearchBarProps {
  onSubmit?: (searchMode: "new", gender: string, query: string) => void;
  placeholder?: string;
  recentSearches?: string[];
  className?: string;
}

export function CompactSearchBar({
  onSubmit,
  placeholder = "What are you looking for?",
  recentSearches = [],
  className = "",
}: CompactSearchBarProps) {
  const { activeSearchParams, updateSearchParam } = useSearchContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Get current gender in display format
  const selectedGender = mapGenderToDisplay(activeSearchParams.gender);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      const query = inputRef.current?.value.trim() || "";
      if (query) {
        onSubmit?.("new", selectedGender, query);
        if (inputRef.current) inputRef.current.value = "";
      }
      setIsExpanded(false);
    } else if (e.key === "Escape") {
      setIsExpanded(false);
    }
  };

  const handleFocus = () => {
    setIsExpanded(true);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Search Input Bar - animates width only, stays in place */}
      <div
        onClick={() => inputRef.current?.focus()}
        className="relative flex cursor-text items-center justify-between bg-white transition-all duration-300 ease-in-out"
        style={{
          width: isExpanded ? "280px" : "224px",
          height: "35px",
          paddingLeft: "10.5px",
          paddingRight: "15px",
          paddingTop: "11px",
          paddingBottom: "11px",
          borderBottom: isExpanded
            ? `1.5px solid ${colors.stroke.pureBlack}`
            : `1px solid ${colors.stroke.pureBlack}`,
        }}
      >
        <input
          ref={inputRef}
          type="text"
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          className="text-input-placeholder flex-1 border-0 bg-transparent text-left text-ink-faint outline-none"
          style={{
            padding: 0,
            margin: 0,
            borderRadius: 0,
          }}
        />

        {/* Arrow Icon - switches from white to black when expanded */}
        <div className="relative h-[14px] w-[14px] flex-shrink-0">
          <Image
            src={isExpanded ? "/icons/arrow-right-black.svg" : "/icons/arrow-right.svg"}
            alt=""
            width={14}
            height={14}
          />
        </div>
      </div>

      {/* Expanded Dropdown - absolutely positioned below search bar with gap */}
      {isExpanded && (
        <div
          className="absolute right-0 top-full overflow-hidden rounded-[8.75px] border bg-white shadow-lg transition-all duration-300 ease-in-out"
          style={{
            width: "280px",
            borderColor: colors.divider.default,
            zIndex: 50,
            marginTop: "7px",
          }}
        >
          <div style={{ padding: "14px" }}>
            {/* Gender options */}
            <div className="flex flex-col gap-[13px]">
              <motion.div
                className="flex items-center gap-[15px]"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
              >
                {["Women's", "Men's", "Both (M+W)", "Kids"].map((gender) => {
                  const isActive = selectedGender === gender;
                  return (
                    <button
                      key={gender}
                      onClick={() =>
                        updateSearchParam("gender", mapGenderToCatalog(gender) || "women")
                      }
                      className="flex flex-col items-start pb-[0.8px]"
                    >
                      <span
                        className={`${isActive ? "text-action-sm text-ink" : "text-action-sm-muted text-ink-faint"}`}
                      >
                        {gender}
                      </span>
                      {isActive && (
                        <motion.div
                          layoutId="gender-underline"
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                          style={{
                            backgroundColor: colors.text.black,
                            height: "1.2px",
                            width: "100%",
                            marginTop: "-0.8px",
                          }}
                        />
                      )}
                    </button>
                  );
                })}
              </motion.div>
            </div>

            {/* Divider */}
            <div
              style={{
                height: "1px",
                backgroundColor: colors.divider.default,
                marginTop: "14px",
                marginBottom: "14px",
              }}
            />

            {/* Recent Searches Section */}
            {recentSearches.length > 0 && (
              <div>
                <p className="text-tag-caps text-ink-muted" style={{ marginBottom: "10.5px" }}>
                  RECENT SEARCHES
                </p>

                <div className="flex flex-col gap-[3.5px]">
                  {recentSearches.map((query, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        // Trigger search with the canned query (always new search)
                        onSubmit?.("new", selectedGender, query);
                        setIsExpanded(false);
                      }}
                      className="flex items-start gap-[10.5px] rounded-[3.5px] px-[7px] py-[7px] text-left transition-colors hover:bg-gray-50"
                    >
                      <div className="mt-[3.5px] h-[10.5px] w-[10.5px] flex-shrink-0">
                        <Image
                          src="/icons/search-small-white.svg"
                          alt=""
                          width={10.5}
                          height={10.5}
                        />
                      </div>
                      <span className="text-action-muted text-ink-softest">{query}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
